import { FormItem } from "./imis.models";

/** Options for registering a custom ASP.NET client-side validator against an evaluation function. */
export interface CustomValidatorOptions {
    id: string;
    text: string;
    evaluationFunction: () => boolean;
    validationGroup?: string;
    triggerValidation?: boolean;
}

/** Wraps an iMIS FormItem's HTML in a jQuery object to support drag-and-drop layout editing across rows and columns. */
export class FormDragDrop {
    data: FormItem;
    layoutRows: FormLayoutColumn[][];
    jHtml: JQuery;

    /**
     * Creates a FormDragDrop wrapper around the given form item, parsing its HTML into a jQuery object and initializing an empty layout.
     * @param formItem The form item whose HTML code will be parsed and edited.
     */
    constructor(formItem: FormItem) {
        this.data = formItem;
        this.layoutRows = [];
        this.jHtml = jQuery(`<div>${formItem.FormHtmlCode}</div>`);
    }

    /**
     * Recomputes the layout identifiers for every row/column in layoutRows, writes them into the wrapped HTML's `layout` element, and updates the form item's HTML code.
     */
    updateLayout() {
        let layoutIdentifiers: string[] = [];
        for (let i = 0; i < this.layoutRows.length; i++) {
            for (let j = 0; j < this.layoutRows[i].length; j++) {
                let column = this.layoutRows[i][j];
                let identifier = this.getLayoutIdentifier(i + 1, j + 1, column.width);
                layoutIdentifiers.push(identifier);
            }
        }

        let layoutIdentifiersText = layoutIdentifiers.join(",");
        this.jHtml.find("layout").text(layoutIdentifiersText);
        this.data.FormHtmlCode = this.getFormHtml();
    }

    /**
     * Builds the layout identifier string for a single row/column/width combination.
     * @param row The 1-based row number.
     * @param column The 1-based column number within the row.
     * @param width The column's width.
     * @returns The identifier in the form "R{row}C{column}W{width}".
     */
    getLayoutIdentifier(row: number, column: number, width: number): string {
        return `R${row}C${column}W${width}`;
    }

    /**
     * Serializes the wrapped jQuery HTML back to a string.
     * @returns The current inner HTML of the wrapped form element.
     */
    getFormHtml(): string {
        return this.jHtml.html();
    }
}

/** A single column within a form layout row, holding its width and associated jQuery element. */
export class FormLayoutColumn {
    width: number;
    jHtml: JQuery;
}
